import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import type { MeProfileKpiResultRowDTO } from "@/modules/users/domain/employee-profile.types";
import {
  Badge,
  Button,
  ScrollArea,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { useAuthState } from "../model/useAuthState";
import { formatKpiProfileMetric } from "../utils/format-kpi-profile-metric";
import { buildProfileKpiExplainSearch } from "../utils/profile-kpi-explain-search";

type ProfileKpiResultsTableProps = {
  rows: MeProfileKpiResultRowDTO[];
};

function periodLabel(row: MeProfileKpiResultRowDTO): string {
  if (row.periodYear == null) return "—";
  if (row.measurementPeriod === "monthly" && row.periodMonth != null) {
    return `${row.periodMonth}/${row.periodYear}`;
  }
  if (row.measurementPeriod === "quarterly" && row.periodQuarter != null) {
    return `Q${row.periodQuarter} ${row.periodYear}`;
  }
  return `${row.periodYear}`;
}

function statusLabel(s: MeProfileKpiResultRowDTO["status"]): string {
  if (s === "pass") return "ຜ່ານ";
  if (s === "fail") return "ຕ່ຳກວ່າເປົ້າ";
  return "ລໍຖ້າ";
}

function StatusBadge({ status }: { status: MeProfileKpiResultRowDTO["status"] }) {
  const label = statusLabel(status);
  if (status === "pass") {
    return (
      <Badge
        variant="outline"
        className="border-emerald-500/40 bg-emerald-500/10 font-normal text-emerald-800 dark:border-emerald-500/30 dark:text-emerald-300"
      >
        {label}
      </Badge>
    );
  }
  if (status === "fail") {
    return (
      <Badge variant="destructive" className="font-normal">
        {label}
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="font-normal">
      {label}
    </Badge>
  );
}

export function ProfileKpiResultsTable({ rows }: ProfileKpiResultsTableProps) {
  const nav = useNavigate();
  const { hasAnyPermission } = useAuthState();
  const canExplain = hasAnyPermission([
    Permissions.kpi.read,
    Permissions.mgmt_okr_kpi.read,
  ]);

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed bg-muted/20 py-10 text-center">
        <p className="text-muted-foreground text-sm">
          ບໍ່ມີ KPI ທີ່ມອບໝາຍແບບລາຍບຸກຄົນ
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
      <ScrollArea className="max-h-[min(60vh,480px)]">
        <Table>
          <TableHeader>
            <TableRow className="border-b bg-muted/40 hover:bg-muted/40">
              <TableHead className="min-w-[140px] font-semibold">KPI</TableHead>
              <TableHead className="w-[100px] whitespace-nowrap font-semibold">
                ງວດ
              </TableHead>
              <TableHead className="font-semibold">ເປົ້າ</TableHead>
              <TableHead className="font-semibold">ຈິງ</TableHead>
              <TableHead className="w-[120px] font-semibold">ສະຖານະ</TableHead>
              <TableHead className="w-[1%] text-end font-semibold" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const explainSearch = buildProfileKpiExplainSearch(row);
              return (
                <TableRow
                  key={row.assignmentId}
                  className="transition-colors hover:bg-muted/30"
                >
                  <TableCell className="align-middle font-medium">
                    {row.kpiName}
                  </TableCell>
                  <TableCell className="whitespace-nowrap align-middle text-muted-foreground text-sm">
                    {periodLabel(row)}
                  </TableCell>
                  <TableCell className="align-middle tabular-nums">
                    {formatKpiProfileMetric(row.targetValue, row.targetUnit)}
                  </TableCell>
                  <TableCell className="align-middle font-medium tabular-nums">
                    {formatKpiProfileMetric(row.actualValue, row.targetUnit)}
                  </TableCell>
                  <TableCell className="align-middle">
                    <StatusBadge status={row.status} />
                  </TableCell>
                  <TableCell className="text-end align-middle">
                    {canExplain && explainSearch ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 shrink-0"
                        onClick={() =>
                          nav({
                            to: "/app/management/okr-kpi/explain",
                            search: explainSearch,
                          })
                        }
                      >
                        ລາຍລະອຽດຜົນ
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}
